/**
 * The executor's ownership on each network as last read, for the sidebar's
 * caption ("Operator · pending owner"). Every read of the Executor page records
 * what it saw, so the caption follows an acceptance without a read of its own;
 * kept per market maker, so another sign-in never reads the previous one's.
 */

import { useSyncExternalStore } from "react";
import type { ExecutorOwnership } from "./api";

/** `null`: no executor on the network, or an API that does not say. */
const readings = new Map<string, ExecutorOwnership | null>();
const listeners = new Set<() => void>();

const keyOf = (companyId: string, chainId: number) => `${companyId}:${chainId}`;

export function recordOwnership(companyId: string, chainId: number, ownership: ExecutorOwnership | null | undefined): void {
  const key = keyOf(companyId, chainId);
  const next = ownership ?? null;
  const before = readings.get(key);
  if (before !== undefined && JSON.stringify(before) === JSON.stringify(next)) return;
  readings.set(key, next);
  for (const listener of [...listeners]) listener();
}

/** Forgets every reading (tests). */
export function clearOwnership(): void {
  readings.clear();
  deskOwners.clear();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `undefined` until the network's executor was read in this visit. */
export function useOwnership(companyId: string, chainId: number): ExecutorOwnership | null | undefined {
  const read = () => readings.get(keyOf(companyId, chainId));
  return useSyncExternalStore(subscribe, read, read);
}

/** AileFX's desk: the owner of its current executor on each network, as last read. */
const deskOwners = new Map<string, string | null>();

export function recordDeskOwner(companyId: string, chainId: number, owner: string | null): void {
  const key = keyOf(companyId, chainId);
  const next = owner === null ? null : owner.toLowerCase();
  if (deskOwners.has(key) && deskOwners.get(key) === next) return;
  deskOwners.set(key, next);
  for (const listener of [...listeners]) listener();
}

/** `undefined` until the desk's executors were read on the network in this visit. */
export function useDeskOwner(companyId: string, chainId: number): string | null | undefined {
  const read = () => deskOwners.get(keyOf(companyId, chainId));
  return useSyncExternalStore(subscribe, read, read);
}
